<template>
  <v-dialog v-model="dialog" max-width="920" scrollable persistent>
    <v-card>
      <v-card-title class="d-flex align-center py-3 px-4">
        <v-icon class="mr-2" color="primary">mdi-filter-cog-outline</v-icon>
        <span>OD Filter Manager</span>
        <v-spacer />
        <v-btn icon variant="text" @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-4" style="max-height: 75vh; overflow-y: auto;">
        <div class="d-flex align-center mb-3">
          <span class="text-body-2 text-medium-emphasis">
            Click name, icon, or color to edit · Click contacts to edit SQL · Campaign fields editable inline
          </span>
          <v-spacer />
          <v-btn size="small" color="primary" prepend-icon="mdi-plus" @click="openCreateDialog">
            Add Custom Filter
          </v-btn>
        </div>

        <template v-for="f in sortedFilters" :key="f.id">
          <div class="filter-card rounded border mb-2">

            <!-- ── Main row ──────────────────────────────────────────── -->
            <div class="d-flex align-center px-3 py-2 gap-2" :class="{ 'opacity-50': !f.is_enabled }">

              <!-- Icon — click to edit -->
              <div v-if="filterEdit.filterId === f.id && filterEdit.field === 'icon'"
                style="width:130px; flex-shrink:0" @click.stop>
                <v-text-field
                  v-model="filterEdit.value"
                  density="compact"
                  variant="outlined"
                  hide-details
                  autofocus
                  :loading="filterEditSaving"
                  @keyup.enter="saveFilterEdit(f)"
                  @keyup.esc="cancelFilterEdit"
                >
                  <template #append-inner>
                    <v-icon :color="f.color" size="16">mdi-{{ filterEdit.value || 'help' }}</v-icon>
                  </template>
                </v-text-field>
              </div>
              <div v-else class="editable-cell flex-shrink-0" title="Click to change icon"
                @click="startFilterEdit(f, 'icon', f.icon)">
                <v-icon :color="f.color" size="22">mdi-{{ f.icon }}</v-icon>
              </div>

              <!-- Color — native color picker (always visible swatch) -->
              <input
                type="color"
                :value="f.color"
                class="color-swatch flex-shrink-0"
                title="Click to change color"
                @change="saveFilterColor(f, $event.target.value)"
              />

              <!-- Name + mode label -->
              <div class="flex-grow-1 min-width-0">
                <div v-if="filterEdit.filterId === f.id && filterEdit.field === 'filter_name'"
                  @click.stop>
                  <v-text-field
                    v-model="filterEdit.value"
                    density="compact"
                    variant="outlined"
                    hide-details
                    autofocus
                    :loading="filterEditSaving"
                    @keyup.enter="saveFilterEdit(f)"
                    @keyup.esc="cancelFilterEdit"
                  />
                </div>
                <div v-else-if="!(f.is_system && f.client_id === 0)"
                  class="font-weight-medium text-body-2 editable-cell"
                  @click="startFilterEdit(f, 'filter_name', f.filter_name)">
                  {{ f.filter_name }}
                  <v-icon size="x-small" class="edit-icon" color="grey">mdi-pencil-outline</v-icon>
                </div>
                <div v-else class="font-weight-medium text-body-2">{{ f.filter_name }}</div>

                <div class="text-caption text-medium-emphasis">
                  {{ modeLabel(f) }}
                  <span v-if="f.client_id > 0" class="ml-2 text-primary">(customised)</span>
                </div>
              </div>

              <!-- Enable / disable toggle -->
              <v-switch
                :model-value="!!f.is_enabled"
                color="primary"
                density="compact"
                hide-details
                @update:model-value="toggleEnabled(f, $event)"
              />

              <!-- Delete / Reset (custom filters only) -->
              <v-btn
                v-if="f.client_id > 0 && !f.is_system"
                icon variant="text" size="small" color="error"
                @click="confirmDelete(f)"
              >
                <v-icon size="18">mdi-delete-outline</v-icon>
              </v-btn>
              <v-btn
                v-else-if="f.client_id > 0 && f.is_system"
                icon variant="text" size="small" title="Reset to default"
                @click="confirmReset(f)"
              >
                <v-icon size="18">mdi-restore</v-icon>
              </v-btn>
            </div>

            <!-- ── Campaign activity strip ───────────────────────────── -->
            <div
              class="campaign-strip d-flex align-center flex-wrap px-3 py-1"
              style="gap:10px; border-top:1px solid rgba(0,0,0,0.06); background:rgba(0,0,0,0.015)"
            >
              <!-- Pause / Resume -->
              <v-btn
                v-if="campaignMap[f.id]?.comm_enabled"
                size="x-small" variant="text" color="purple"
                prepend-icon="mdi-pause-circle-outline"
                :loading="!!fieldSaving[f.id + '_comm_enabled']"
                @click="toggleActive(f)">
                Pause
              </v-btn>
              <v-btn
                v-else
                size="x-small" variant="text" color="success"
                prepend-icon="mdi-play-circle-outline"
                :loading="!!fieldSaving[f.id + '_comm_enabled']"
                @click="toggleActive(f)">
                Resume
              </v-btn>

              <v-divider vertical style="height:16px; align-self:center" />

              <!-- Email channel — click to edit template -->
              <v-tooltip location="top"
                :text="campaignMap[f.id]?.enable_email ? 'Email enabled — edit template' : 'Email disabled — edit template'">
                <template #activator="{ props }">
                  <v-btn v-bind="props" icon size="x-small" variant="text"
                    :color="campaignMap[f.id]?.enable_email ? 'blue' : 'grey-lighten-1'"
                    @click="openEmailEditor(f)">
                    <v-icon size="16">mdi-email-outline</v-icon>
                  </v-btn>
                </template>
              </v-tooltip>

              <!-- SMS channel — click to edit template -->
              <v-tooltip location="top"
                :text="campaignMap[f.id]?.enable_sms ? 'SMS enabled — edit template' : 'SMS disabled — edit template'">
                <template #activator="{ props }">
                  <v-btn v-bind="props" icon size="x-small" variant="text"
                    :color="campaignMap[f.id]?.enable_sms ? 'green' : 'grey-lighten-1'"
                    @click="openSmsEditor(f)">
                    <v-icon size="16">mdi-message-text-outline</v-icon>
                  </v-btn>
                </template>
              </v-tooltip>

              <v-divider vertical style="height:16px; align-self:center" />

              <!-- Tag — inline select -->
              <div v-if="inlineEdit.filterId === f.id && inlineEdit.field === 'tag'"
                style="min-width:150px" @click.stop>
                <v-select
                  v-model="inlineEdit.value"
                  :items="[{ tag_id: null, tag_name: '— None —' }, ...tags]"
                  item-title="tag_name"
                  item-value="tag_id"
                  density="compact"
                  variant="outlined"
                  hide-details
                  autofocus
                  :loading="!!fieldSaving[f.id + '_tag_id']"
                  @update:model-value="saveTag(f, $event)"
                  @keyup.esc="cancelInlineEdit"
                />
              </div>
              <div v-else class="editable-cell" @click="startInlineEdit(f, 'tag', campaignMap[f.id]?.tag_id || null)">
                <v-chip v-if="tagName(campaignMap[f.id]?.tag_id)"
                  size="x-small" label prepend-icon="mdi-tag-outline" color="primary" variant="tonal">
                  {{ tagName(campaignMap[f.id]?.tag_id) }}
                </v-chip>
                <span v-else class="text-caption text-medium-emphasis">No tag</span>
                <v-icon size="x-small" class="edit-icon" color="grey">mdi-pencil-outline</v-icon>
              </div>

              <v-divider vertical style="height:16px; align-self:center" />

              <!-- Contact Tag narrowing/exclusion — opens dialog -->
              <v-tooltip location="top" :text="tagFilterSummary(f.id)">
                <template #activator="{ props }">
                  <v-btn v-bind="props" icon size="x-small" variant="text"
                    :color="tagFilterIconColor(f.id)"
                    @click="openTagFilterEditor(f)">
                    <v-icon size="16">mdi-tag-search-outline</v-icon>
                  </v-btn>
                </template>
              </v-tooltip>

              <v-divider vertical style="height:16px; align-self:center" />

              <!-- Schedule — click to edit -->
              <div class="editable-cell" @click="openScheduleEdit(f)">
                <v-icon size="13" class="mr-1"
                  :color="campaignMap[f.id]?.schedule_config?.scheduleType === 2 ? 'purple' : 'teal'">
                  {{ campaignMap[f.id]?.schedule_config?.scheduleType === 2 ? 'mdi-repeat' : 'mdi-clock-outline' }}
                </v-icon>
                <span class="text-caption">{{ scheduleDescription(campaignMap[f.id]?.schedule_config) }}</span>
                <v-icon size="x-small" class="edit-icon" color="grey">mdi-pencil-outline</v-icon>
              </div>

              <v-divider vertical style="height:16px; align-self:center" />

              <!-- Contacts count — click icon+count to edit SQL -->
              <div class="editable-cell" title="Click to edit filter SQL" @click="openSqlEditor(f)">
                <v-icon size="13" color="primary">mdi-account-group-outline</v-icon>
                <v-progress-circular v-if="contactsLoadingMap[f.id]" indeterminate size="12" width="2" />
                <span v-else class="text-caption">
                  {{ contactsMap[f.id] !== undefined ? contactsMap[f.id] + ' patients' : '—' }}
                </span>
                <v-icon size="11" class="edit-icon" color="grey">mdi-pencil-outline</v-icon>
              </div>
              <v-btn icon size="x-small" variant="text" title="Refresh contact count"
                :disabled="!!contactsLoadingMap[f.id]"
                @click.stop="loadContactsCount(f.id)">
                <v-icon size="12">mdi-refresh</v-icon>
              </v-btn>
            </div>

          </div>
        </template>
      </v-card-text>

      <v-divider />

      <v-card-actions class="px-4 py-3">
        <v-btn variant="text" @click="close">Close</v-btn>
      </v-card-actions>
    </v-card>

    <!-- ── SQL editor dialog ──────────────────────────────────────────── -->
    <v-dialog v-model="sqlDialog" max-width="700" persistent>
      <v-card>
        <v-card-title class="d-flex align-center py-3 px-4">
          <v-icon class="me-2" color="primary">mdi-database-edit-outline</v-icon>
          Filter SQL — {{ sqlFilter?.filter_name }}
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-4" style="max-height: 75vh; overflow-y: auto;">
          <template v-if="['sql','contacts_sql'].includes(sqlFilter?.execution_mode)">
            <v-alert v-if="sqlFilter?.execution_mode === 'contacts_sql'" type="info" variant="tonal" density="compact" class="mb-3">
              Runs against your DSS database. Must SELECT a <code>contact_id</code> column. Common tables:
              <code>contacts</code> (contact_id, first_name, last_name, email, ph1/ph2/ph3, contact_type, dt_stamp),
              <code>contact_tags</code> + <code>tags</code> (tag-based targeting), <code>contact_types</code>.
              Results are always limited to your own client's contacts regardless of what the query selects.
            </v-alert>
            <v-textarea
              ref="sqlDraftRef"
              v-model="sqlDraft"
              :label="sqlFilter?.execution_mode === 'contacts_sql' ? 'SQL Query (must SELECT contact_id)' : 'SQL Query (must SELECT PatNum)'"
              variant="outlined"
              density="compact"
              rows="10"
              class="mb-2"
              style="font-family: monospace; font-size: 13px"
              :placeholder="sqlFilter?.execution_mode === 'contacts_sql' ? 'SELECT contact_id, ... FROM contacts WHERE ...' : 'SELECT patient.PatNum, ... FROM ...'"
              @keydown="onTemplateKey($event, 'sqlDraft')"
              @input="onSqlInput($event, 'sqlDraft')"
            />

            <div class="d-flex gap-2 mb-3">
              <v-btn
                size="small" variant="tonal" color="primary"
                prepend-icon="mdi-play-outline"
                :loading="previewLoading"
                @click="runPreview">
                Preview Results
              </v-btn>
              <v-btn
                v-if="sqlFilter?.client_id === 0"
                size="small" variant="tonal" prepend-icon="mdi-code-tags"
                :loading="sqlLoading"
                @click="loadDefaultSqlInDialog">
                Load Default SQL
              </v-btn>
            </div>

            <v-alert v-if="sqlError" type="error" density="compact" class="mb-2">{{ sqlError }}</v-alert>
            <v-alert v-if="previewError" type="warning" density="compact" class="mb-2">{{ previewError }}</v-alert>

            <!-- ── Preview results table ──────────────────────────────── -->
            <template v-if="previewColumns.length">
              <div class="d-flex align-center mb-1">
                <span class="text-caption text-medium-emphasis">
                  Showing {{ previewRows.length }} row{{ previewRows.length !== 1 ? 's' : '' }}
                  <span v-if="previewTotal >= 100"> (first 100 — save and run filter for full results)</span>
                </span>
              </div>
              <div class="preview-table-wrap">
                <table class="preview-table">
                  <thead>
                    <tr>
                      <th v-for="col in previewColumns" :key="col">{{ col }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, ri) in previewRows" :key="ri">
                      <td v-for="col in previewColumns" :key="col">{{ row[col] ?? '' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>
            <div v-else-if="!previewLoading && previewError === null && previewColumns.length === 0 && previewRows.length === 0 && sqlDraft"
              class="text-caption text-medium-emphasis text-center py-2">
            </div>
          </template>
          <template v-else>
            <v-alert type="info" variant="tonal">
              <strong>{{ sqlFilter?.filter_name }}</strong> uses the
              {{ sqlFilter?.execution_mode === 'recalls_api' ? 'Recalls API' : 'Lab Cases API' }}
              to fetch contacts — SQL is not applicable for this filter.
            </v-alert>
          </template>
        </v-card-text>
        <v-card-actions class="px-4 py-3">
          <v-spacer />
          <v-btn variant="text" @click="sqlDialog = false">Cancel</v-btn>
          <v-btn
            v-if="['sql','contacts_sql'].includes(sqlFilter?.execution_mode)"
            color="primary" variant="flat"
            :loading="sqlSaving"
            @click="saveSqlEdit">
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── Schedule editor dialog ─────────────────────────────────────── -->
    <v-dialog v-model="scheduleDialog" max-width="520" :persistent="false">
      <v-card>
        <v-card-title class="py-3 px-4">Edit Schedule</v-card-title>
        <v-divider />
        <v-card-text class="pa-4">
          <RecurrenceEditor v-model="scheduleDraft" />
        </v-card-text>
        <v-divider />
        <v-card-actions class="px-4 py-3">
          <v-spacer />
          <v-btn variant="text" @click="scheduleDialog = false">Cancel</v-btn>
          <v-btn color="primary" variant="flat"
            :loading="scheduleFilterId != null && !!fieldSaving[scheduleFilterId + '_schedule_config']"
            @click="applySchedule">
            Apply
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── Contact Tag narrowing/exclusion editor ───────────────────────── -->
    <v-dialog v-model="tagFilterDialog" max-width="520" :persistent="false">
      <v-card>
        <v-card-title class="py-3 px-4">Narrow Recipients by Contact Tag</v-card-title>
        <v-divider />
        <v-card-text class="pa-4" v-if="tagFilterDraft">
          <div class="text-caption text-medium-emphasis mb-2">
            Applied after the filter's own results — narrow down to and/or suppress contacts by their Contact Tags.
          </div>

          <v-select
            v-model="tagFilterDraft.tag_filter_ids"
            :items="tags"
            item-title="tag_name"
            item-value="tag_id"
            label="Narrow to contacts with tag"
            multiple chips closable-chips
            variant="outlined" density="compact" hide-details class="mb-1"
          />
          <v-btn-toggle
            v-if="tagFilterDraft.tag_filter_ids.length > 1"
            v-model="tagFilterDraft.tag_filter_mode"
            mandatory density="compact" variant="outlined" divided class="mb-3 mt-1">
            <v-btn value="any" size="small">Any tag</v-btn>
            <v-btn value="all" size="small">All tags</v-btn>
          </v-btn-toggle>
          <div v-else class="mb-3" />

          <v-select
            v-model="tagFilterDraft.tag_exclude_ids"
            :items="tags"
            item-title="tag_name"
            item-value="tag_id"
            label="Exclude contacts with tag (e.g. Don't Text Me)"
            multiple chips closable-chips
            variant="outlined" density="compact" hide-details
            color="error" base-color="error"
          />
        </v-card-text>
        <v-divider />
        <v-card-actions class="px-4 py-3">
          <v-spacer />
          <v-btn variant="text" @click="tagFilterDialog = false">Cancel</v-btn>
          <v-btn color="primary" variant="flat"
            :loading="tagFilterFilterId != null && !!fieldSaving[tagFilterFilterId + '_tag_filter_ids_tag_filter_mode_tag_exclude_ids']"
            @click="applyTagFilter">
            Apply
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── Email template editor ──────────────────────────────────────── -->
    <v-dialog v-model="emailDialog" max-width="600" persistent>
      <v-card>
        <v-card-title class="d-flex align-center py-3 px-4">
          <v-icon class="me-2" color="blue">mdi-email-outline</v-icon>
          Email Template
          <v-spacer />
          <v-switch v-model="emailEdit.enableEmail" label="Enabled" color="success"
            density="compact" hide-details class="ms-4" />
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-4">
          <v-text-field
            ref="emailSubjectRef"
            v-model="emailEdit.emailSubject"
            label="Subject"
            variant="outlined"
            density="compact"
            class="mb-3"
            placeholder="e.g. Follow-up from your recent visit, [NameF]"
            @keydown="onTemplateKey($event, 'emailSubject')"
            @input="onTemplateInput($event, 'emailSubject')"
          />
          <v-textarea
            ref="emailTemplateRef"
            v-model="emailEdit.emailTemplate"
            label="Email Body (HTML)"
            variant="outlined"
            density="compact"
            rows="8"
            class="mb-2"
            placeholder="<p>Hi [NameF],</p><p>...</p>"
            @keydown="onTemplateKey($event, 'emailTemplate')"
            @input="onTemplateInput($event, 'emailTemplate')"
          />
          <div class="text-caption text-medium-emphasis">
            Type <code>[</code> to insert a variable
          </div>
        </v-card-text>
        <v-card-actions class="px-4 py-3">
          <v-spacer />
          <v-btn variant="text" @click="emailDialog = false">Cancel</v-btn>
          <v-btn color="blue" variant="flat" :loading="dialogSaving" @click="saveEmailEdit">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── SMS template editor ────────────────────────────────────────── -->
    <v-dialog v-model="smsDialog" max-width="520" persistent>
      <v-card>
        <v-card-title class="d-flex align-center py-3 px-4">
          <v-icon class="me-2" color="green">mdi-message-text-outline</v-icon>
          SMS Template
          <v-spacer />
          <v-switch v-model="smsEdit.enableSMS" label="Enabled" color="success"
            density="compact" hide-details class="ms-4" />
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-4">
          <v-textarea
            ref="smsTemplateRef"
            v-model="smsEdit.smsTemplate"
            label="SMS Message"
            variant="outlined"
            density="compact"
            rows="5"
            class="mb-3"
            placeholder="Hi [NameF], this is a reminder from our office. [TrackingURL]"
            @keydown="onTemplateKey($event, 'smsTemplate')"
            @input="onTemplateInput($event, 'smsTemplate')"
          />
          <v-text-field
            v-model="smsEdit.smsLinkDestination"
            label="Link destination URL (for [TrackingURL])"
            variant="outlined"
            density="compact"
            prepend-inner-icon="mdi-link"
            placeholder="https://yoursite.com/page"
            class="mb-2"
          />
          <div class="text-caption text-medium-emphasis">
            Type <code>[</code> to insert a variable
          </div>
        </v-card-text>
        <v-card-actions class="px-4 py-3">
          <v-spacer />
          <v-btn variant="text" @click="smsDialog = false">Cancel</v-btn>
          <v-btn color="green" variant="flat" :loading="dialogSaving" @click="saveSmsEdit">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── Create custom filter dialog ───────────────────────────────── -->
    <v-dialog v-model="createDialog" max-width="640" persistent>
      <v-card>
        <v-card-title class="d-flex align-center py-3 px-4">
          <v-icon class="me-2" color="primary">mdi-filter-plus-outline</v-icon>
          New Custom Filter
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-4">
          <v-text-field
            v-model="createForm.filter_name"
            label="Filter Name"
            variant="outlined"
            density="compact"
            class="mb-3"
          />
          <v-btn-toggle
            v-model="createForm.execution_mode"
            mandatory
            density="compact"
            variant="outlined"
            color="primary"
            class="mb-3"
          >
            <v-btn value="sql" size="small">OD SQL</v-btn>
            <v-btn value="contacts_sql" size="small">DSS Contacts SQL</v-btn>
          </v-btn-toggle>
          <div class="d-flex mb-3" style="gap:12px">
            <v-text-field
              v-model="createForm.icon"
              label="Icon (mdi name without mdi- prefix)"
              variant="outlined"
              density="compact"
              hint="e.g. account-reactivate-outline"
              persistent-hint
              class="flex-1-1"
            >
              <template #append-inner>
                <v-icon :color="createForm.color" size="20">mdi-{{ createForm.icon }}</v-icon>
              </template>
            </v-text-field>
            <div class="d-flex flex-column align-center justify-center" style="gap:4px">
              <span class="text-caption text-medium-emphasis">Color</span>
              <input type="color" v-model="createForm.color" class="color-swatch-lg" />
            </div>
          </div>
          <v-alert v-if="createForm.execution_mode === 'contacts_sql'" type="info" variant="tonal" density="compact" class="mb-3">
            Runs against your DSS database. Must SELECT a <code>contact_id</code> column. Common tables:
            <code>contacts</code> (contact_id, first_name, last_name, email, ph1/ph2/ph3, contact_type, dt_stamp),
            <code>contact_tags</code> + <code>tags</code> (tag-based targeting), <code>contact_types</code>.
            Results are always limited to your own client's contacts regardless of what the query selects.
          </v-alert>
          <v-textarea
            ref="createFormSqlRef"
            v-model="createForm.sql_query"
            :label="createForm.execution_mode === 'contacts_sql' ? 'SQL Query (must SELECT contact_id)' : 'SQL Query (must SELECT PatNum)'"
            variant="outlined"
            density="compact"
            rows="8"
            style="font-family: monospace; font-size: 13px"
            :placeholder="createForm.execution_mode === 'contacts_sql' ? 'SELECT contact_id, ... FROM contacts WHERE ...' : 'SELECT patient.PatNum, ... FROM ...'"
            @keydown="onTemplateKey($event, 'createFormSql')"
            @input="onSqlInput($event, 'createFormSql')"
          />
          <v-alert v-if="createError" type="error" density="compact" class="mt-2">{{ createError }}</v-alert>
        </v-card-text>
        <v-card-actions class="px-4 py-3">
          <v-spacer />
          <v-btn variant="text" @click="createDialog = false">Cancel</v-btn>
          <v-btn color="primary" variant="flat" :loading="createSaving" @click="saveCreate">Create</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete confirmation -->
    <v-dialog v-model="deleteDialog" max-width="380">
      <v-card>
        <v-card-title>Delete Filter</v-card-title>
        <v-card-text>Remove <strong>{{ deletingFilter?.filter_name }}</strong>? This cannot be undone.</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="error" variant="flat" :loading="deleting" @click="doDelete">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Reset confirmation -->
    <v-dialog v-model="resetDialog" max-width="400">
      <v-card>
        <v-card-title>Reset to Default</v-card-title>
        <v-card-text>
          Remove your customisation for <strong>{{ resetFilter?.filter_name }}</strong> and restore the system default?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="resetDialog = false">Cancel</v-btn>
          <v-btn color="warning" variant="flat" :loading="resetting" @click="doReset">Reset</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>

  <!-- ── Variable picker — teleported to body so it clears dialog overflow ── -->
  <Teleport to="body">
    <div
      v-if="varMenu.show"
      class="var-picker"
      :style="{ top: varMenu.menuY + 'px', left: varMenu.menuX + 'px', minWidth: varMenu.menuWidth + 'px' }"
      @mousedown.prevent
    >
      <template v-for="(v, i) in varMenuItems" :key="v.label">
        <div v-if="i > 0 && v.group !== varMenuItems[i - 1].group"
          class="var-picker-divider">{{ groupLabel(v.group) }}</div>
        <div
          class="var-picker-item"
          :class="{ 'var-picker-item--active': varMenu.index === i }"
          @mousedown.prevent="insertVar(v.label, { cursorOffset: v.cursorOffset })"
        >
          <span class="var-picker-label">{{ v.label }}</span>
          <span class="var-picker-desc">{{ v.desc }}</span>
        </div>
      </template>
      <div v-if="!varMenuItems.length" class="var-picker-empty">No matches</div>
    </div>
  </Teleport>

  <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="6000" location="bottom">
    {{ snackbar.text }}
    <template #actions>
      <v-btn variant="text" @click="snackbar.show = false">Close</v-btn>
    </template>
  </v-snackbar>
</template>

<script>
import { mapGetters } from 'vuex';
import { DateTime } from 'luxon';
import OdService from '@/services/odService';
import TagService from '@/services/tagService';
import RecurrenceEditor from '@/components/BulkComm/RecurrenceEditor.vue';

// contacts_sql query-builder assistant: curated table list (mirrors the backend's
// CURATED_TABLES in contactsSqlFilter.service.js) and WHERE-clause snippets. These
// are date functions appropriate to what the curated tables actually contain —
// contacts.dt_stamp/Birthdate are native DATE/DATETIME, not epoch-ms BIGINT (that
// convention exists elsewhere in this schema but not on these tables), so no
// FROM_UNIXTIME(.../1000)-style snippet is offered here.
const SQL_CURATED_TABLES = ['contacts', 'contact_tags', 'tags', 'contact_types'];
const SQL_SNIPPET_ITEMS = [
  { label: "IN ('', '')",  desc: 'Match any of a list',        group: 'snippet' },
  { label: "LIKE '%%'",    desc: 'Partial text match',         group: 'snippet', cursorOffset: -2 },
  { label: 'BETWEEN  AND ', desc: 'Range match',                group: 'snippet', cursorOffset: -5 },
  { label: 'IS NULL',      desc: '',                           group: 'snippet' },
  { label: 'IS NOT NULL',  desc: '',                           group: 'snippet' },
  { label: 'DATE_SUB(NOW(), INTERVAL 30 DAY)', desc: 'Relative date (30 days ago)', group: 'snippet' },
  { label: 'CURDATE()',    desc: "Today's date",               group: 'snippet' },
  { label: "DATE_FORMAT(dt_stamp, '%Y-%m-%d')", desc: 'Format a date column', group: 'snippet' },
  { label: 'YEAR(Birthdate) = ', desc: 'Filter by year',       group: 'snippet' },
];
const GROUP_LABELS = { standard: 'Variables', sql: 'SQL columns', table: 'Tables', column: 'Columns', value: 'Values', snippet: 'Snippets' };

export default {
  name: 'ODFilterManager',
  components: { RecurrenceEditor },
  emits: ['close'],

  data() {
    return {
      dialog: true,

      // ── Campaign data per filter ──
      campaignMap: {},
      fieldSaving: {},
      snackbar: { show: false, text: '', color: 'error' },

      // ── Inline campaign field edit (tag) ──
      inlineEdit: { filterId: null, field: null, value: null },

      // ── Filter row inline edit (name / icon) ──
      filterEdit: { filterId: null, field: null, value: '' },
      filterEditSaving: false,

      // ── Schedule editor dialog ──
      scheduleDialog: false,
      scheduleDraft: null,
      scheduleFilterId: null,

      // ── Contact Tag narrowing/exclusion editor ──
      tagFilterDialog: false,
      tagFilterDraft: null,
      tagFilterFilterId: null,

      // ── SQL editor dialog ──
      sqlDialog: false,
      sqlFilter: null,
      sqlDraft: '',
      sqlLoading: false,
      sqlSaving: false,
      sqlError: null,
      previewLoading: false,
      previewError: null,
      previewColumns: [],
      previewRows: [],
      previewTotal: 0,

      // ── Email / SMS template dialogs ──
      dialogFilter: null,
      dialogSaving: false,
      emailDialog: false,
      emailEdit: { enableEmail: false, emailSubject: '', emailTemplate: '' },
      smsDialog: false,
      smsEdit: { enableSMS: false, smsTemplate: '', smsLinkDestination: '' },

      // ── Create filter dialog ──
      createDialog: false,
      createForm: { filter_name: '', icon: 'filter', color: '#607D8B', sql_query: '', execution_mode: 'sql' },
      createSaving: false,
      createError: null,

      // ── Contacts count per filter ──
      contactsMap: {},
      contactsLoadingMap: {},

      // ── Tags ──
      tags: [],

      // ── Variable picker ──
      varMenu: { show: false, field: null, search: '', index: 0, triggerPos: -1, menuX: 0, menuY: 0, menuWidth: 240 },
      sqlColumns: [],   // column names parsed from the active filter's SQL

      // ── contacts_sql query-builder assistant ──
      sqlContext: null,        // result of buildSqlContext() for the SQL field currently being edited
      sqlSchemaMeta: null,     // { tables: [{name, columns: [{name, type, nullable, valueHint}]}] }, fetched once
      sqlValueHintCache: {},   // "table.column" -> [{value, label}], populated on demand

      // ── Delete / Reset ──
      deleteDialog: false,
      deletingFilter: null,
      deleting: false,
      resetDialog: false,
      resetFilter: null,
      resetting: false,
    };
  },

  computed: {
    ...mapGetters('frontdesk', ['odFilterList']),
    ...mapGetters(['clientTimeZone']),
    varMenuItems() {
      if (this.varMenu.field === 'sqlDraft' || this.varMenu.field === 'createFormSql') {
        return this.sqlMenuItems;
      }
      const emailVars = [
        { label: '[NameF]',           desc: 'First name',         group: 'standard' },
        { label: '[NameL]',           desc: 'Last name',          group: 'standard' },
        { label: '[TrackingURL]',     desc: 'Tracking link',      group: 'standard' },
        { label: '[EmailDisclaimer]', desc: 'Unsubscribe footer', group: 'standard' },
      ];
      const smsVars = [
        { label: '[NameF]',       desc: 'First name',    group: 'standard' },
        { label: '[NameL]',       desc: 'Last name',     group: 'standard' },
        { label: '[TrackingURL]', desc: 'Tracking link', group: 'standard' },
      ];
      const standard = this.varMenu.field === 'smsTemplate' ? smsVars : emailVars;
      const standardLabels = new Set(standard.map(v => v.label));
      const sqlVars = this.sqlColumns
        .filter(col => !standardLabels.has(`[${col}]`))
        .map(col => ({ label: `[${col}]`, desc: 'SQL column', group: 'sql' }));
      const all = [...standard, ...sqlVars];
      if (!this.varMenu.search) return all;
      const q = this.varMenu.search.toLowerCase();
      return all.filter(v => v.label.toLowerCase().includes(q));
    },
    sortedFilters() {
      return [...this.odFilterList].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    },
    // Items for the SQL-mode floating menu (table/column/value suggestions +
    // WHERE-clause snippets), driven by sqlContext (set by onSqlInput/buildSqlContext).
    sqlMenuItems() {
      const ctx = this.sqlContext;
      if (!ctx) return [];
      // Strip underscores before comparing so typing "tagname" still matches the real
      // column "tag_name" — snake_case columns are common here (tag_name, contact_id,
      // vendor_cat, ...) and it's natural to type the fragment without the underscore.
      const normalize = (s) => s.toLowerCase().replace(/_/g, '');
      const q = normalize(this.varMenu.search || '');
      const matches = (label) => !q || normalize(label).includes(q);
      const items = [];

      if (ctx.kind === 'table') {
        for (const t of SQL_CURATED_TABLES) {
          if (matches(t)) items.push({ label: t, desc: 'table', group: 'table' });
        }
        return items;
      }

      if (ctx.kind === 'column') {
        const tableNames = ctx.scopeTable ? [ctx.scopeTable]
          : (ctx.aliasTables && ctx.aliasTables.length ? ctx.aliasTables : SQL_CURATED_TABLES);
        for (const t of tableNames) {
          const tableMeta = this.sqlSchemaMeta?.tables?.find(x => x.name === t);
          if (!tableMeta) continue;
          for (const col of tableMeta.columns) {
            if (matches(col.name)) items.push({ label: col.name, desc: `${t} · ${col.type}`, group: 'column' });
          }
        }
      }

      if (ctx.kind === 'value') {
        const key = `${ctx.table}.${ctx.column}`;
        const values = this.sqlValueHintCache[key] || [];
        for (const v of values) {
          const label = String(v.value);
          if (matches(label) || (v.label && matches(String(v.label)))) {
            items.push({ label, desc: v.label != null && String(v.label) !== label ? String(v.label) : '', group: 'value' });
          }
        }
      }

      if (ctx.kind === 'snippet' || ctx.includeSnippets) {
        for (const s of SQL_SNIPPET_ITEMS) {
          if (matches(s.label)) items.push(s);
        }
      }

      return items;
    },
  },

  watch: {
    emailDialog(v) { if (!v) this.varMenu.show = false; },
    smsDialog(v)   { if (!v) this.varMenu.show = false; },
    sqlDialog(v)   { if (!v) { this.varMenu.show = false; this.sqlContext = null; } },
    createDialog(v) { if (!v) { this.varMenu.show = false; this.sqlContext = null; } },
  },

  async created() {
    await Promise.all([this.loadCampaignStatuses(), this.loadTags()]);
  },

  methods: {
    _blankCampaign() {
      return {
        comm_enabled: false,
        enable_email: false,
        enable_sms: false,
        email_subject: '',
        email_template: '',
        sms_template: '',
        sms_link_destination: '',
        tag_id: null,
        tag_filter_ids: null,
        tag_filter_mode: 'any',
        tag_exclude_ids: null,
        schedule_config: null,
        timezone: this.clientTimeZone || 'America/New_York',
      };
    },

    modeLabel(f) {
      if (f.execution_mode === 'sql') return 'SQL query';
      if (f.execution_mode === 'recalls_api') return 'Recalls API';
      if (f.execution_mode === 'contacts_sql') return 'DSS Contacts SQL';
      return 'Lab Cases API';
    },

    scheduleDescription(cfg) {
      if (!cfg) return 'Not scheduled';
      return cfg.description || (cfg.scheduleType === 2 ? 'Recurring' : 'One-time send');
    },

    tagName(tagId) {
      if (!tagId) return null;
      return this.tags.find(t => t.tag_id === tagId)?.tag_name || null;
    },

    close() {
      this.dialog = false;
      this.$emit('close');
    },

    // ── Variable picker ───────────────────────────────────────────────────
    onTemplateInput(e, field) {
      const value   = e.target.value;
      const pos     = e.target.selectionStart;
      const before  = value.slice(0, pos);
      const bracket = before.lastIndexOf('[');
      if (bracket === -1) { this.varMenu.show = false; return; }
      const after = before.slice(bracket + 1);
      if (after.includes(']') || after.includes(' ') || after.includes('\n')) {
        this.varMenu.show = false;
        return;
      }
      const rect = e.target.getBoundingClientRect();
      this.varMenu = {
        show: true, field,
        search: after, index: 0, triggerPos: bracket,
        menuX: rect.left, menuY: rect.bottom + 4, menuWidth: Math.max(rect.width, 240),
      };
    },

    onTemplateKey(e, field) {
      if (!this.varMenu.show || this.varMenu.field !== field) return;
      if (e.key === 'Escape') {
        e.preventDefault(); this.varMenu.show = false;
      } else if (e.key === 'Tab' || e.key === 'Enter') {
        if (this.varMenuItems.length) {
          e.preventDefault();
          const item = this.varMenuItems[this.varMenu.index];
          this.insertVar(item?.label, { cursorOffset: item?.cursorOffset });
        } else this.varMenu.show = false;
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.varMenu.index = Math.min(this.varMenu.index + 1, this.varMenuItems.length - 1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.varMenu.index = Math.max(this.varMenu.index - 1, 0);
      }
    },

    // ── contacts_sql query-builder assistant ────────────────────────────────
    // Fetches the curated schema metadata once per component session (lazily, on
    // first need) — cheap and static enough to cache indefinitely rather than
    // re-fetch per dialog open.
    async ensureSqlSchemaMeta() {
      if (this.sqlSchemaMeta) return;
      try {
        const res = await OdService.getContactsSqlSchema();
        this.sqlSchemaMeta = res?.data || { tables: [] };
      } catch (_) {
        // Leave null — sqlMenuItems/buildSqlContext degrade gracefully (snippets
        // still work, table/column suggestions just won't appear).
      }
    },

    // Builds { alias|tableName -> tableName } from the FROM/JOIN clauses in the
    // whole current draft (not just before the cursor, since WHERE almost always
    // comes after FROM/JOIN — restricting to before-cursor would lose column
    // suggestions when editing an earlier clause). Flat, top-level scan only — no
    // nested-subquery-aware alias resolution in this v1 (mirrors the same scope
    // limitation already noted on extractSelectClause/parseSqlColumns).
    _sqlAliasMap(text) {
      // A bare (non-AS) alias is ambiguous with a following SQL keyword — e.g.
      // "FROM contacts WHERE ..." would otherwise capture "WHERE" as an alias.
      // Excluding common clause/join keywords keeps that from happening.
      const NOT_AN_ALIAS = new Set([
        'ON', 'WHERE', 'JOIN', 'INNER', 'LEFT', 'RIGHT', 'OUTER', 'FULL', 'CROSS',
        'GROUP', 'ORDER', 'LIMIT', 'HAVING', 'UNION', 'AND', 'OR', 'SELECT',
      ]);
      const map = {};
      const re = /\b(?:FROM|JOIN)\s+(\w+)(?:\s+(?:AS\s+)?(\w+))?/gi;
      let m;
      while ((m = re.exec(text))) {
        const table = m[1];
        if (!SQL_CURATED_TABLES.includes(table)) continue;
        map[table] = table;
        if (m[2] && !NOT_AN_ALIAS.has(m[2].toUpperCase())) map[m[2]] = table;
      }
      return map;
    },

    onSqlInput(e, field) {
      const isContactsSql = field === 'sqlDraft'
        ? this.sqlFilter?.execution_mode === 'contacts_sql'
        : this.createForm.execution_mode === 'contacts_sql';
      if (!isContactsSql) { this.varMenu.show = false; this.sqlContext = null; return; }

      const value = e.target.value;
      const pos   = e.target.selectionStart;
      const ctx   = this.buildSqlContext(value, pos);
      if (!ctx) { this.varMenu.show = false; this.sqlContext = null; return; }

      this.sqlContext = ctx;
      const rect = e.target.getBoundingClientRect();
      this.varMenu = {
        show: true, field,
        search: ctx.fragment, index: 0, triggerPos: ctx.triggerPos,
        menuX: rect.left, menuY: rect.bottom + 4, menuWidth: Math.max(rect.width, 280),
      };
      if (ctx.kind === 'value') this.debouncedFetchValueHints(ctx.table, ctx.column);
    },

    // Heuristic (not a real SQL parser) context detector — given the full text and
    // cursor position, decides what the floating menu should offer next: a table
    // name, a column name (optionally scoped to an alias), a value-hint lookup, or
    // just WHERE-clause snippets. Returns null when nothing sensible to suggest.
    buildSqlContext(text, pos) {
      const before = text.slice(0, pos);
      const aliasMap = this._sqlAliasMap(text);

      // 1. Table name, right after FROM/JOIN.
      let m = before.match(/\b(?:FROM|JOIN)\s+(\w*)$/i);
      if (m) return { kind: 'table', fragment: m[1], triggerPos: pos - m[1].length };

      // 2. Alias-qualified column: "alias.partial"
      m = before.match(/\b(\w+)\.(\w*)$/);
      if (m && aliasMap[m[1]]) {
        return { kind: 'column', scopeTable: aliasMap[m[1]], fragment: m[2], triggerPos: pos - m[2].length };
      }

      // 3. Value-hint trigger: "[alias.]column = " / "IN (" / "LIKE " with an optional
      //    partial value already typed.
      m = before.match(/\b(?:(\w+)\.)?(\w+)\s*(?:=|IN\s*\(|LIKE)\s*['"]?(\w*)$/i);
      if (m) {
        const [, aliasPrefix, colName, fragment] = m;
        const table = aliasPrefix ? aliasMap[aliasPrefix] : this._sqlUnambiguousTable(colName);
        if (table) {
          const tableMeta = this.sqlSchemaMeta?.tables?.find(t => t.name === table);
          const colMeta = tableMeta?.columns?.find(c => c.name === colName);
          if (colMeta?.valueHint) {
            return { kind: 'value', table, column: colName, fragment, triggerPos: pos - fragment.length };
          }
        }
      }

      // 4. Unqualified column, after SELECT / comma / WHERE / AND / OR / ON.
      m = before.match(/\b(?:SELECT|WHERE|AND|OR|ON)\s+(\w*)$/i) || before.match(/,\s*(\w*)$/);
      if (m) {
        const isWhereBoundary = /\b(?:WHERE|AND|OR)\s+\w*$/i.test(before);
        return {
          kind: 'column',
          scopeTable: null,
          aliasTables: Object.values(aliasMap).filter((v, i, a) => a.indexOf(v) === i),
          fragment: m[1],
          triggerPos: pos - m[1].length,
          includeSnippets: isWhereBoundary,
        };
      }

      return null;
    },

    // Finds the single curated table (among those referenced so far, or all curated
    // tables if none referenced yet) whose schema has this column name. Returns null
    // if zero or more than one table matches (ambiguous — no value trigger).
    _sqlUnambiguousTable(colName) {
      const tables = this.sqlSchemaMeta?.tables || [];
      const owners = tables.filter(t => t.columns.some(c => c.name === colName)).map(t => t.name);
      return owners.length === 1 ? owners[0] : null;
    },

    debouncedFetchValueHints(table, column) {
      clearTimeout(this._valueHintTimer);
      this._valueHintTimer = setTimeout(async () => {
        const key = `${table}.${column}`;
        if (this.sqlValueHintCache[key] !== undefined) return;
        try {
          const res = await OdService.getContactsSqlValueHints(table, column);
          this.sqlValueHintCache = { ...this.sqlValueHintCache, [key]: res?.data?.values || [] };
        } catch (_) {
          this.sqlValueHintCache = { ...this.sqlValueHintCache, [key]: [] };
        }
      }, 300);
    },

    insertVar(label, opts = {}) {
      if (!label) { this.varMenu.show = false; return; }
      const field      = this.varMenu.field;
      const triggerPos = this.varMenu.triggerPos;
      const el         = this._varFieldEl(field);
      const cursorPos  = el ? el.selectionStart : triggerPos + this.varMenu.search.length + 1;
      const current    = this._varFieldValue(field);
      this._varFieldSet(field, current.slice(0, triggerPos) + label + current.slice(cursorPos));
      this.varMenu.show = false;
      this.sqlContext = null;
      this.$nextTick(() => {
        if (el) {
          const p = triggerPos + label.length + (opts.cursorOffset || 0);
          el.setSelectionRange(p, p); el.focus();
        }
      });
    },

    _varFieldEl(field) {
      const ref = this.$refs[field + 'Ref'];
      return ref?.$el?.querySelector('textarea') || ref?.$el?.querySelector('input') || null;
    },

    _varFieldValue(field) {
      if (field === 'emailSubject')  return this.emailEdit.emailSubject;
      if (field === 'emailTemplate') return this.emailEdit.emailTemplate;
      if (field === 'smsTemplate')   return this.smsEdit.smsTemplate;
      if (field === 'sqlDraft')      return this.sqlDraft || '';
      if (field === 'createFormSql') return this.createForm.sql_query || '';
      return '';
    },

    _varFieldSet(field, value) {
      if (field === 'emailSubject')  this.emailEdit.emailSubject  = value;
      else if (field === 'emailTemplate') this.emailEdit.emailTemplate = value;
      else if (field === 'smsTemplate')   this.smsEdit.smsTemplate     = value;
      else if (field === 'sqlDraft')      this.sqlDraft = value;
      else if (field === 'createFormSql') this.createForm.sql_query = value;
    },

    groupLabel(group) {
      return GROUP_LABELS[group] || '';
    },

    async loadSqlColumns(filter) {
      this.sqlColumns = [];
      if (!['sql', 'contacts_sql'].includes(filter.execution_mode)) return;
      let sql = filter.sql_query;
      if (!sql) {
        // System default — fetch from backend
        try {
          const res = await OdService.getOdFilterDefaultSql(filter.id);
          sql = res?.data?.sql || '';
        } catch (_) { return; }
      }
      this.sqlColumns = this.parseSqlColumns(sql);
    },

    _sqlHasOutputColumn(sql, columnName) {
      const re = new RegExp(`^${columnName}$`, 'i');
      return this.parseSqlColumns(sql).some(col => re.test(col));
    },

    _sqlHasPatNumOutput(sql) {
      return this._sqlHasOutputColumn(sql, 'PatNum');
    },

    _sqlHasContactIdOutput(sql) {
      return this._sqlHasOutputColumn(sql, 'contact_id');
    },

    parseSqlColumns(sql) {
      if (!sql) return [];
      // Extract the SELECT clause — everything between SELECT and the outer-level FROM
      // that terminates the SELECT list. Scanning for the FIRST "FROM" with a plain
      // regex would incorrectly stop inside a subquery/derived-table expression used
      // within the SELECT list itself (e.g. a scalar subquery column), truncating or
      // missing entirely any real column that comes after it — so this tracks
      // parenthesis depth and only stops at a FROM that isn't nested inside one.
      const upper = sql.replace(/\r\n/g, '\n').trim();
      const selectPrefix = upper.match(/^SELECT\s+/i);
      if (!selectPrefix) return [];
      const body = upper.slice(selectPrefix[0].length);
      const fromRe = /\bFROM\b/gi;
      let fromDepth = 0, scanned = 0, fromMatch, clauseEnd = -1;
      while ((fromMatch = fromRe.exec(body))) {
        for (let i = scanned; i < fromMatch.index; i++) {
          if (body[i] === '(') fromDepth++;
          else if (body[i] === ')') fromDepth--;
        }
        scanned = fromMatch.index;
        if (fromDepth === 0) { clauseEnd = fromMatch.index; break; }
      }
      if (clauseEnd === -1) return [];
      const clause = body.slice(0, clauseEnd).trim();

      // Split by commas, skipping commas inside parentheses
      const exprs = [];
      let depth = 0, buf = '';
      for (const ch of clause) {
        if (ch === '(') depth++;
        else if (ch === ')') depth--;
        else if (ch === ',' && depth === 0) { exprs.push(buf.trim()); buf = ''; continue; }
        buf += ch;
      }
      if (buf.trim()) exprs.push(buf.trim());

      const skip = new Set(['distinct', '*', '1']);
      return exprs.map(expr => {
        expr = expr.replace(/--[^\n]*/g, '').trim();
        // Explicit alias: ... AS alias or ... AS `alias`
        const asMatch = expr.match(/\bAS\s+`?(\w+)`?\s*$/i);
        if (asMatch) return asMatch[1];
        // table.column — take the column part
        const dotMatch = expr.match(/\.(\w+)\s*$/);
        if (dotMatch) return dotMatch[1];
        // bare word
        const wordMatch = expr.match(/(\w+)\s*$/);
        return wordMatch ? wordMatch[1] : null;
      }).filter(col => col && !skip.has(col.toLowerCase()));
    },

    // ── Tags ─────────────────────────────────────────────────────────────
    async loadTags() {
      try {
        const res = await TagService.getTags();
        const grouped = res.data || {};
        this.tags = Array.isArray(grouped) ? grouped : Object.values(grouped).flat();
      } catch (_) {
        this.tags = [];
      }
    },

    // ── Campaign badge map ────────────────────────────────────────────────
    async loadCampaignStatuses() {
      const map = {};
      await Promise.all(
        this.odFilterList.map(async (f) => {
          try {
            const res = await OdService.getFilterCampaign(f.id);
            if (res?.data?.data) map[f.id] = res.data.data;
          } catch (_) {}
        })
      );
      this.campaignMap = map;
    },

    // ── Filter row inline edit (name / icon) ─────────────────────────────
    startFilterEdit(filter, field, value) {
      this.filterEdit = { filterId: filter.id, field, value };
    },

    cancelFilterEdit() {
      this.filterEdit = { filterId: null, field: null, value: '' };
    },

    async saveFilterEdit(filter) {
      const { field, value } = this.filterEdit;
      if (!value?.trim()) { this.cancelFilterEdit(); return; }
      this.filterEditSaving = true;
      try {
        await OdService.updateOdFilter(filter.id, { [field]: value.trim() });
        await this.$store.dispatch('frontdesk/loadOdFilters');
        this.cancelFilterEdit();
      } catch (e) {
        console.error('Failed to save filter field', e);
      } finally {
        this.filterEditSaving = false;
      }
    },

    async saveFilterColor(filter, color) {
      try {
        await OdService.updateOdFilter(filter.id, { color });
        await this.$store.dispatch('frontdesk/loadOdFilters');
      } catch (e) {
        console.error('Failed to save color', e);
      }
    },

    // ── SQL editor dialog ─────────────────────────────────────────────────
    openSqlEditor(filter) {
      this.sqlFilter = filter;
      this.sqlDraft = filter.sql_query || '';
      this.sqlError = null;
      this.previewColumns = [];
      this.previewRows = [];
      this.previewTotal = 0;
      this.previewError = null;
      this.sqlDialog = true;
      if (filter.execution_mode === 'contacts_sql') this.ensureSqlSchemaMeta();
    },

    async loadDefaultSqlInDialog() {
      this.sqlLoading = true;
      this.sqlError = null;
      try {
        const res = await OdService.getOdFilterDefaultSql(this.sqlFilter.id);
        this.sqlDraft = res?.data?.sql || '';
      } catch (_) {
        this.sqlError = 'Could not load default SQL';
      } finally {
        this.sqlLoading = false;
      }
    },

    async runPreview() {
      const sql = this.sqlDraft?.trim();
      if (!sql) { this.previewError = 'Enter a SQL query first'; return; }
      this.previewLoading = true;
      this.previewError = null;
      this.sqlError = null;
      this.previewColumns = [];
      this.previewRows = [];
      this.previewTotal = 0;
      try {
        const res = await OdService.previewFilterSql(sql, this.sqlFilter?.execution_mode);
        this.previewColumns = res?.data?.columns || [];
        this.previewRows    = res?.data?.rows    || [];
        this.previewTotal   = res?.data?.total   ?? this.previewRows.length;
      } catch (e) {
        this.previewError = e?.response?.data?.message || 'Query failed';
      } finally {
        this.previewLoading = false;
      }
    },

    async saveSqlEdit() {
      const sql = this.sqlDraft?.trim();
      const isContactsSql = this.sqlFilter?.execution_mode === 'contacts_sql';
      if (!sql) { this.sqlError = 'SQL query is required'; return; }
      if (!sql.toUpperCase().startsWith('SELECT')) { this.sqlError = 'Query must be a SELECT statement'; return; }
      if (isContactsSql) {
        if (!this._sqlHasContactIdOutput(sql)) { this.sqlError = 'Query must SELECT a column named contact_id (e.g. contacts.contact_id or contact_id AS contact_id)'; return; }
      } else {
        if (!this._sqlHasPatNumOutput(sql)) { this.sqlError = 'Query must SELECT a column named PatNum (e.g. patient.PatNum or p.PatNum AS PatNum)'; return; }
      }
      this.sqlSaving = true;
      this.sqlError = null;
      try {
        await OdService.updateOdFilter(this.sqlFilter.id, { sql_query: sql });
        await this.$store.dispatch('frontdesk/loadOdFilters');
        this.sqlDialog = false;
      } catch (e) {
        this.sqlError = e?.response?.data?.message || 'Save failed';
      } finally {
        this.sqlSaving = false;
      }
    },

    // ── Campaign field saves ──────────────────────────────────────────────
    async saveField(filter, updates) {
      const savingKey = filter.id + '_' + Object.keys(updates).join('_');
      this.fieldSaving[savingKey] = true;
      try {
        const existing = this.campaignMap[filter.id] || {};
        const merged = { ...this._blankCampaign(), ...existing, ...updates };
        const payload = {
          ...merged,
          comm_enabled: merged.comm_enabled ? 1 : 0,
          enable_email: merged.enable_email ? 1 : 0,
          enable_sms: merged.enable_sms ? 1 : 0,
        };
        const res = await OdService.saveFilterCampaign(filter.id, payload);
        const saved = res?.data?.data || merged;
        this.campaignMap = { ...this.campaignMap, [filter.id]: saved };
        if (res?.data?.warning) {
          this.showSnackbar(res.data.warning, 'warning');
        }
        return true;
      } catch (e) {
        console.error('saveField failed', e);
        this.showSnackbar(e?.response?.data?.message || 'Failed to save campaign settings.', 'error');
        return false;
      } finally {
        delete this.fieldSaving[savingKey];
        this.fieldSaving = { ...this.fieldSaving };
      }
    },

    async toggleActive(filter) {
      const current = this.campaignMap[filter.id] || {};
      const activating = !current.comm_enabled;

      // Lightweight client-side pre-checks for instant feedback on the common case —
      // the server (saveCampaign) remains authoritative and re-validates regardless.
      if (activating) {
        if (!current.enable_email && !current.enable_sms) {
          this.showSnackbar('At least one channel (email or SMS) must be enabled to activate this campaign.', 'error');
          return;
        }
        const scheduleError = this._validateScheduleClient(current.schedule_config, current.timezone);
        if (scheduleError) {
          this.showSnackbar(scheduleError, 'error');
          return;
        }
      }

      await this.saveField(filter, { comm_enabled: activating ? 1 : 0 });
    },

    _validateScheduleClient(schedule_config, timezone) {
      if (!schedule_config) return 'A schedule is required to activate this campaign.';
      const { scheduleType, scheduledAt, cronExpression } = schedule_config;
      if (scheduleType === 1) {
        if (!scheduledAt) return 'A send date/time is required for a one-time campaign.';
        const ts = DateTime.fromISO(scheduledAt, { zone: timezone || this.clientTimeZone }).toMillis();
        if (Number.isNaN(ts)) return 'The scheduled date/time is not valid.';
        if (ts <= Date.now()) return 'The scheduled date/time must be in the future.';
      } else if (scheduleType === 2) {
        // Full cron syntax validation happens server-side; just check it's set here.
        if (!cronExpression) return 'A valid recurring schedule is required to activate this campaign.';
      } else {
        return 'A valid schedule is required to activate this campaign.';
      }
      return null;
    },

    showSnackbar(text, color = 'error') {
      this.snackbar = { show: true, text, color };
    },

    // ── Email template dialog ─────────────────────────────────────────────
    openEmailEditor(filter) {
      this.dialogFilter = filter;
      const c = this.campaignMap[filter.id] || {};
      this.emailEdit = {
        enableEmail: !!c.enable_email,
        emailSubject: c.email_subject || '',
        emailTemplate: c.email_template || '',
      };
      this.emailDialog = true;
      this.loadSqlColumns(filter);
    },

    async saveEmailEdit() {
      this.dialogSaving = true;
      try {
        await this.saveField(this.dialogFilter, {
          enable_email: this.emailEdit.enableEmail ? 1 : 0,
          email_subject: this.emailEdit.emailSubject,
          email_template: this.emailEdit.emailTemplate,
        });
        this.emailDialog = false;
      } finally {
        this.dialogSaving = false;
      }
    },

    // ── SMS template dialog ───────────────────────────────────────────────
    openSmsEditor(filter) {
      this.dialogFilter = filter;
      const c = this.campaignMap[filter.id] || {};
      this.smsEdit = {
        enableSMS: !!c.enable_sms,
        smsTemplate: c.sms_template || '',
        smsLinkDestination: c.sms_link_destination || '',
      };
      this.smsDialog = true;
      this.loadSqlColumns(filter);
    },

    async saveSmsEdit() {
      this.dialogSaving = true;
      try {
        await this.saveField(this.dialogFilter, {
          enable_sms: this.smsEdit.enableSMS ? 1 : 0,
          sms_template: this.smsEdit.smsTemplate,
          sms_link_destination: this.smsEdit.smsLinkDestination,
        });
        this.smsDialog = false;
      } finally {
        this.dialogSaving = false;
      }
    },

    // ── Inline tag edit ───────────────────────────────────────────────────
    startInlineEdit(filter, field, value) {
      this.inlineEdit = { filterId: filter.id, field, value };
    },

    cancelInlineEdit() {
      this.inlineEdit = { filterId: null, field: null, value: null };
    },

    async saveTag(filter, tagId) {
      this.cancelInlineEdit();
      await this.saveField(filter, { tag_id: tagId || null });
    },

    // ── Schedule dialog ───────────────────────────────────────────────────
    openScheduleEdit(filter) {
      this.scheduleFilterId = filter.id;
      const cfg = this.campaignMap[filter.id]?.schedule_config;
      this.scheduleDraft = cfg ? JSON.parse(JSON.stringify(cfg)) : null;
      this.scheduleDialog = true;
    },

    async applySchedule() {
      const filter = this.odFilterList.find(f => f.id === this.scheduleFilterId);
      if (!filter) { this.scheduleDialog = false; return; }
      const wasInactive = !this.campaignMap[filter.id]?.comm_enabled;

      const scheduleSaved = await this.saveField(filter, { schedule_config: this.scheduleDraft });
      this.scheduleDialog = false;
      if (!scheduleSaved) return; // saveField already surfaced the specific error

      // Setting a schedule implies the user wants this campaign to actually run —
      // activate it automatically if it wasn't already. If activation itself fails
      // (e.g. no channel enabled), the schedule stays saved regardless and saveField
      // has already shown the specific reason activation didn't succeed.
      if (wasInactive) {
        const activated = await this.saveField(filter, { comm_enabled: 1 });
        if (activated) {
          this.showSnackbar('Schedule saved — campaign activated.', 'success');
        }
      }
    },

    // ── Contact Tag narrowing/exclusion dialog ──────────────────────────────
    openTagFilterEditor(filter) {
      this.tagFilterFilterId = filter.id;
      const c = this.campaignMap[filter.id] || {};
      this.tagFilterDraft = {
        tag_filter_ids: Array.isArray(c.tag_filter_ids) ? [...c.tag_filter_ids] : [],
        tag_filter_mode: c.tag_filter_mode || 'any',
        tag_exclude_ids: Array.isArray(c.tag_exclude_ids) ? [...c.tag_exclude_ids] : [],
      };
      this.tagFilterDialog = true;
    },

    async applyTagFilter() {
      const filter = this.odFilterList.find(f => f.id === this.tagFilterFilterId);
      if (!filter || !this.tagFilterDraft) { this.tagFilterDialog = false; return; }
      const saved = await this.saveField(filter, {
        tag_filter_ids: this.tagFilterDraft.tag_filter_ids.length ? this.tagFilterDraft.tag_filter_ids : null,
        tag_filter_mode: this.tagFilterDraft.tag_filter_mode,
        tag_exclude_ids: this.tagFilterDraft.tag_exclude_ids.length ? this.tagFilterDraft.tag_exclude_ids : null,
      });
      if (saved) {
        this.tagFilterDialog = false;
        this.loadContactsCount(filter.id);
      }
    },

    tagFilterIconColor(filterId) {
      const c = this.campaignMap[filterId];
      if (c?.tag_exclude_ids?.length) return 'error';
      if (c?.tag_filter_ids?.length) return 'primary';
      return 'grey-lighten-1';
    },

    tagFilterSummary(filterId) {
      const c = this.campaignMap[filterId];
      const inc = Array.isArray(c?.tag_filter_ids) ? c.tag_filter_ids : [];
      const exc = Array.isArray(c?.tag_exclude_ids) ? c.tag_exclude_ids : [];
      if (!inc.length && !exc.length) return 'Narrow recipients by Contact Tag';
      const parts = [];
      if (inc.length) parts.push(`Narrowed to ${c.tag_filter_mode === 'all' ? 'all' : 'any'} of: ${inc.map(id => this.tagName(id)).filter(Boolean).join(', ')}`);
      if (exc.length) parts.push(`Excludes: ${exc.map(id => this.tagName(id)).filter(Boolean).join(', ')}`);
      return parts.join(' · ');
    },

    // ── Contacts count ────────────────────────────────────────────────────
    async loadContactsCount(filterId) {
      this.contactsLoadingMap[filterId] = true;
      this.contactsMap = { ...this.contactsMap };
      try {
        const res = await OdService.executeOdFilter(filterId, 0);
        const list = res?.data?.data || [];
        this.contactsMap = { ...this.contactsMap, [filterId]: list.length };
      } catch (_) {
        this.contactsMap = { ...this.contactsMap, [filterId]: undefined };
      } finally {
        delete this.contactsLoadingMap[filterId];
        this.contactsLoadingMap = { ...this.contactsLoadingMap };
      }
    },

    // ── Create filter dialog ──────────────────────────────────────────────
    openCreateDialog() {
      this.createForm = { filter_name: '', icon: 'filter', color: '#607D8B', sql_query: '', execution_mode: 'sql' };
      this.createError = null;
      this.createDialog = true;
      this.ensureSqlSchemaMeta();
    },

    async saveCreate() {
      const sql = this.createForm.sql_query?.trim();
      const isContactsSql = this.createForm.execution_mode === 'contacts_sql';
      if (!this.createForm.filter_name?.trim()) { this.createError = 'Filter name is required'; return; }
      if (!sql) { this.createError = 'SQL query is required'; return; }
      if (!sql.toUpperCase().startsWith('SELECT')) { this.createError = 'Query must be a SELECT statement'; return; }
      if (isContactsSql) {
        if (!this._sqlHasContactIdOutput(sql)) { this.createError = 'Query must SELECT a column named contact_id (e.g. contacts.contact_id or contact_id AS contact_id)'; return; }
      } else {
        if (!this._sqlHasPatNumOutput(sql)) { this.createError = 'Query must SELECT a column named PatNum (e.g. patient.PatNum or p.PatNum AS PatNum)'; return; }
      }
      this.createSaving = true;
      this.createError = null;
      try {
        await OdService.createOdFilter({
          filter_name: this.createForm.filter_name.trim(),
          icon: this.createForm.icon,
          color: this.createForm.color,
          sql_query: sql,
          execution_mode: this.createForm.execution_mode,
        });
        await this.$store.dispatch('frontdesk/loadOdFilters');
        this.createDialog = false;
      } catch (e) {
        this.createError = e?.response?.data?.message || 'Create failed';
      } finally {
        this.createSaving = false;
      }
    },

    // ── Toggle filter enabled ─────────────────────────────────────────────
    async toggleEnabled(filter, val) {
      try {
        await OdService.updateOdFilter(filter.id, { is_enabled: val ? 1 : 0 });
        await this.$store.dispatch('frontdesk/loadOdFilters');
      } catch (e) {
        console.error('toggleEnabled failed', e);
      }
    },

    // ── Delete ────────────────────────────────────────────────────────────
    confirmDelete(filter) { this.deletingFilter = filter; this.deleteDialog = true; },
    async doDelete() {
      this.deleting = true;
      try {
        await OdService.deleteOdFilter(this.deletingFilter.id);
        await this.$store.dispatch('frontdesk/loadOdFilters');
        this.deleteDialog = false;
        this.deletingFilter = null;
      } catch (e) { console.error('Delete failed', e); }
      finally { this.deleting = false; }
    },

    // ── Reset to default ──────────────────────────────────────────────────
    confirmReset(filter) { this.resetFilter = filter; this.resetDialog = true; },
    async doReset() {
      this.resetting = true;
      try {
        await OdService.deleteOdFilter(this.resetFilter.id);
        await this.$store.dispatch('frontdesk/loadOdFilters');
        this.resetDialog = false;
        this.resetFilter = null;
      } catch (e) { console.error('Reset failed', e); }
      finally { this.resetting = false; }
    },
  },
};
</script>

<style scoped>
.filter-card {
  border: 1px solid rgba(0, 0, 0, 0.08);
  overflow: hidden;
}
.campaign-strip {
  min-height: 32px;
}
.gap-2 {
  gap: 8px;
}
.editable-cell {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  border-radius: 4px;
  padding: 2px 4px;
}
.editable-cell:hover {
  background: rgba(0, 0, 0, 0.05);
}
.editable-cell .edit-icon {
  opacity: 0;
  transition: opacity 0.15s;
}
.editable-cell:hover .edit-icon {
  opacity: 1;
}
/* SQL preview table */
.preview-table-wrap {
  overflow-x: auto;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  max-height: 260px;
  overflow-y: auto;
}
.preview-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  font-family: monospace;
  white-space: nowrap;
}
.preview-table th {
  position: sticky;
  top: 0;
  background: #f5f5f5;
  padding: 5px 10px;
  text-align: left;
  border-bottom: 1px solid rgba(0, 0, 0, 0.15);
  font-weight: 600;
  font-family: sans-serif;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(0, 0, 0, 0.6);
}
.preview-table td {
  padding: 4px 10px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.preview-table tr:last-child td { border-bottom: none; }
.preview-table tr:hover td { background: rgba(0, 0, 0, 0.03); }

/* Native color picker styled as a small swatch */
.color-swatch {
  width: 20px;
  height: 20px;
  border: none;
  border-radius: 3px;
  padding: 0;
  cursor: pointer;
  background: none;
}
.color-swatch::-webkit-color-swatch-wrapper { padding: 0; }
.color-swatch::-webkit-color-swatch { border: 1px solid rgba(0,0,0,0.2); border-radius: 3px; }
/* Larger color swatch for create dialog */
.color-swatch-lg {
  width: 40px;
  height: 40px;
  border: 1px solid rgba(0,0,0,0.2);
  border-radius: 6px;
  padding: 2px;
  cursor: pointer;
}
.color-swatch-lg::-webkit-color-swatch-wrapper { padding: 0; }
.color-swatch-lg::-webkit-color-swatch { border: none; border-radius: 4px; }
</style>

<style>
/* Teleported outside the dialog — cannot be scoped */
.var-picker {
  position: fixed;
  z-index: 3000;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 6px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.14);
  max-height: 220px;
  overflow-y: auto;
}
.var-picker-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 14px;
  cursor: pointer;
  font-size: 13px;
  transition: background 0.1s;
}
.var-picker-item:hover {
  background: rgba(0, 0, 0, 0.05);
}
.var-picker-item--active {
  background: #1867c0;
  color: #fff;
}
.var-picker-label {
  font-family: monospace;
  font-weight: 600;
  white-space: nowrap;
}
.var-picker-desc {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.5);
  white-space: nowrap;
}
.var-picker-item--active .var-picker-desc {
  color: rgba(255, 255, 255, 0.65);
}
.var-picker-divider {
  padding: 4px 14px 2px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.38);
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  margin-top: 2px;
}
.var-picker-empty {
  padding: 8px 14px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.4);
}
</style>
